/**
 * Admin Marketplaces Page
 *
 * Displays marketplace analytics including 24hr and all-time volumes and fee revenue.
 */

import { useEffect, useState } from 'react';
import {
  getMarketplaceStats,
  getMarketplaceBackfillStatus,
  triggerMarketplaceBackfill,
  type MarketplaceBackfillStatusResponse,
} from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import type { MarketplaceStatsResponse, MarketplaceStats } from '../types/admin.js';

export function AdminMarketplacesPage() {
  const [data, setData] = useState<MarketplaceStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [backfillStatus, setBackfillStatus] = useState<MarketplaceBackfillStatusResponse | null>(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillTriggering, setBackfillTriggering] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getMarketplaceStats();
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load marketplace statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadBackfillStatus = async () => {
    try {
      setBackfillLoading(true);
      const result = await getMarketplaceBackfillStatus();
      setBackfillStatus(result);
    } catch (err: any) {
      console.error('Failed to load backfill status:', err);
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleTriggerBackfill = async (collectionId?: string) => {
    try {
      setBackfillTriggering(true);
      setBackfillMessage(null);
      const result = await triggerMarketplaceBackfill(collectionId, collectionId ? 1 : 10);
      setBackfillMessage({
        type: 'success',
        text: result.message,
      });
      // Refresh backfill status after triggering
      await loadBackfillStatus();
    } catch (err: any) {
      setBackfillMessage({
        type: 'error',
        text: err.message || 'Failed to trigger backfill',
      });
    } finally {
      setBackfillTriggering(false);
    }
  };

  useEffect(() => {
    loadStats();
    loadBackfillStatus();
  }, []);

  const formatXrp = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString('en-US');
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(3)}%`;
  };

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Marketplaces</h1>
          <p className="mt-2 text-sm text-gray-600">
            Analytics for NFT sales across different marketplaces
          </p>
          {data && (
            <p className="mt-1 text-xs text-gray-400">
              Last updated: {new Date(data.generatedAt).toLocaleString()}
            </p>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <ErrorDisplay
            error={error}
            onRetry={loadStats}
          />
        )}

        {/* Loading State */}
        {loading && !data && (
          <div className="animate-pulse">
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        )}

        {/* Data Display */}
        {data && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-500">24h Sales</h3>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {formatNumber(data.totals.stats24h.saleCount)}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-500">24h Volume</h3>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {formatXrp(data.totals.stats24h.volumeXrp)} XRP
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-500">All-Time Sales</h3>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {formatNumber(data.totals.statsAllTime.saleCount)}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-500">All-Time Volume</h3>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {formatXrp(data.totals.statsAllTime.volumeXrp)} XRP
                </p>
              </div>
            </div>

            {/* Marketplace Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Marketplace Breakdown</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Marketplace
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fee
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        24h Sales
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        24h Volume
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        24h Revenue
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        All-Time Sales
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        All-Time Volume
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        All-Time Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[...data.marketplaces].sort((a, b) => b.stats24h.estimatedFeesXrp - a.stats24h.estimatedFeesXrp).map((marketplace) => (
                      <tr
                        key={marketplace.id}
                        className={marketplace.isOwnMarketplace ? 'bg-blue-50' : ''}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="font-medium text-gray-900">
                              {marketplace.name}
                            </span>
                            {marketplace.isOwnMarketplace && (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                Our Platform
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatPercent(marketplace.feePercentage)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatNumber(marketplace.stats24h.saleCount)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatXrp(marketplace.stats24h.volumeXrp)} XRP
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-green-600">
                          {formatXrp(marketplace.stats24h.estimatedFeesXrp)} XRP
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatNumber(marketplace.statsAllTime.saleCount)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatXrp(marketplace.statsAllTime.volumeXrp)} XRP
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-green-600">
                          {formatXrp(marketplace.statsAllTime.estimatedFeesXrp)} XRP
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-gray-100 font-semibold">
                      <td className="px-4 py-4 whitespace-nowrap text-gray-900">
                        Total
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        -
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatNumber(data.totals.stats24h.saleCount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatXrp(data.totals.stats24h.volumeXrp)} XRP
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-green-600">
                        {formatXrp(data.totals.stats24h.estimatedFeesXrp)} XRP
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatNumber(data.totals.statsAllTime.saleCount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatXrp(data.totals.statsAllTime.volumeXrp)} XRP
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-green-600">
                        {formatXrp(data.totals.statsAllTime.estimatedFeesXrp)} XRP
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Note */}
            <div className="mt-4 text-sm text-gray-500">
              <p>
                <strong>Note:</strong> Revenue is estimated based on current fee percentages.
                Only sales brokered through these marketplaces are tracked.
              </p>
            </div>

            {/* Backfill Section */}
            <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Marketplace Data Backfill</h2>
                  <p className="text-sm text-gray-500">
                    Re-fetch transaction history to populate marketplace data for existing sales
                  </p>
                </div>
                <button
                  onClick={() => loadBackfillStatus()}
                  disabled={backfillLoading}
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  {backfillLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {backfillMessage && (
                <div
                  className={`px-4 py-3 ${
                    backfillMessage.type === 'success'
                      ? 'bg-green-50 text-green-800'
                      : 'bg-red-50 text-red-800'
                  }`}
                >
                  {backfillMessage.text}
                </div>
              )}

              {backfillStatus && (
                <div className="p-4">
                  {/* Backfill Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-sm text-gray-500">Total Sales</div>
                      <div className="text-lg font-semibold">
                        {formatNumber(backfillStatus.summary.totalSales)}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded p-3">
                      <div className="text-sm text-green-600">With Marketplace</div>
                      <div className="text-lg font-semibold text-green-700">
                        {formatNumber(backfillStatus.summary.salesWithMarketplace)}
                      </div>
                    </div>
                    <div className="bg-yellow-50 rounded p-3">
                      <div className="text-sm text-yellow-600">Needs Backfill</div>
                      <div className="text-lg font-semibold text-yellow-700">
                        {formatNumber(backfillStatus.summary.salesWithoutMarketplace)}
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded p-3">
                      <div className="text-sm text-blue-600">Collections to Process</div>
                      <div className="text-lg font-semibold text-blue-700">
                        {formatNumber(backfillStatus.summary.collectionsNeedingBackfill)}
                      </div>
                    </div>
                  </div>

                  {/* Trigger All Button */}
                  {backfillStatus.summary.collectionsNeedingBackfill > 0 && (
                    <div className="mb-4">
                      <button
                        onClick={() => handleTriggerBackfill()}
                        disabled={backfillTriggering}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {backfillTriggering ? 'Triggering...' : 'Trigger Backfill (Top 10 Collections)'}
                      </button>
                    </div>
                  )}

                  {/* Collections needing backfill */}
                  {backfillStatus.collections.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Collection
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                              Transactions
                            </th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {backfillStatus.collections.slice(0, 20).map((collection) => (
                            <tr key={collection.collectionId}>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {collection.name || 'Unnamed Collection'}
                                </div>
                                <div className="text-xs text-gray-500 font-mono">
                                  {collection.creatorAddress.substring(0, 12)}... / {collection.taxon}
                                </div>
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                                {formatNumber(collection.transactionsNeedingBackfill)}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-center">
                                {collection.hasActiveJob ? (
                                  <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                                    In Progress
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                    Pending
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-right">
                                <button
                                  onClick={() => handleTriggerBackfill(collection.collectionId)}
                                  disabled={backfillTriggering || collection.hasActiveJob}
                                  className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Backfill
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {backfillStatus.collections.length > 20 && (
                        <div className="px-4 py-2 text-sm text-gray-500 text-center">
                          Showing 20 of {backfillStatus.collections.length} collections
                        </div>
                      )}
                    </div>
                  )}

                  {backfillStatus.summary.salesWithoutMarketplace === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      All sales have marketplace data. No backfill needed.
                    </div>
                  )}
                </div>
              )}

              {!backfillStatus && backfillLoading && (
                <div className="p-4 animate-pulse">
                  <div className="h-24 bg-gray-200 rounded"></div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
